/* eslint-disable @typescript-eslint/no-unused-vars */
import { GetNextPageParamFunction, QueryFunction, QueryKey, type UseQueryOptions } from 'react-query';

import { DEFAULT_PAGINATION, Pagination } from '@/hooks/useEntityList';

import {
  FilterTalentsParams,
  GetMidotUsersParams,
  GetOrganizationItemsParams,
  GetProcessTalentsParams,
  GetRolesParams,
  GetUserOrganizationAssignmentsParams,
  GetUserRoleAssignmentsByLocationParams,
  MidotRolesResponseDTO,
  MidotUsersResponseDTO,
  OrganizationDTO,
  OrganizationItemsResponseDto,
  OrganizationRequestDTO,
  PagedProcessTalentsResponseDTO,
  ProcessQueryDTO,
  ProcessResponseDTO,
  TalentListRequestDTO,
  TalentsResponseDTO,
  useFilterOrganizationsHook,
  useFilterProcessesHook,
  useFilterTalentsHook,
  useGetMidotUsersHook,
  useGetOrganizationItemsHook,
  useGetProcessTalentsHook,
  useGetRolesHook,
  useGetUserOrganizationAssignmentsHook,
  useGetUserRoleAssignmentsByLocationHook,
  UserLocationRoleAssignmentsResponse,
  UserOrganizationAssignmentsResponseDTO
} from './generated';

const defaultGetNextPageParam =
  <QueryFnData, QueryFnDataItem>(
    getItems: (response: QueryFnData) => QueryFnDataItem[] | undefined,
    pageSize: number | undefined
  ) =>
  (lastPage: QueryFnData, allPages: QueryFnData[]) => {
    if (!lastPage) return allPages.length;

    const items = getItems(lastPage);

    if (!items) return allPages.length;

    const responsePageSize = items.length || 0;
    const expectedPageSize = pageSize || DEFAULT_PAGINATION.size!;

    if (responsePageSize >= expectedPageSize) return allPages.length;

    // Current page is smaller than expected - no more pages to load
    return undefined;
  };

const defaultGetQueryFn = <QueryData extends Pagination, QueryResponse>(
  fetchFn: (queryData: QueryData, signal?: AbortSignal) => Promise<QueryResponse>,
  queryData: QueryData
) => {
  const queryFn: QueryFunction<QueryResponse, QueryKey> = ({ pageParam, signal }) =>
    fetchFn(
      {
        ...queryData,
        page: pageParam ?? 0,
        size: queryData?.size ?? DEFAULT_PAGINATION?.size
      },
      signal
    );

  return queryFn;
};

export const useCustomFilterOrganizationsQueryOptions = (
  options: UseQueryOptions<OrganizationDTO[]>,
  queryData: { organizationRequestDTO: OrganizationRequestDTO },
  _endpoint: { url: string }
): UseQueryOptions<OrganizationDTO[]> => {
  const filterOrganizations = useFilterOrganizationsHook();

  const queryFn = defaultGetQueryFn(filterOrganizations, queryData.organizationRequestDTO);

  const getNextPageParam: GetNextPageParamFunction<OrganizationDTO[]> = defaultGetNextPageParam(
    response => response,
    queryData.organizationRequestDTO?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomFilterOrganizationItemsQueryOptions = (
  options: UseQueryOptions<OrganizationItemsResponseDto>,
  queryData: { organizationSlug: string; params: GetOrganizationItemsParams },
  _endpoint: { url: string }
): UseQueryOptions<OrganizationItemsResponseDto> => {
  const getOrganizationItems = useGetOrganizationItemsHook();

  const queryFn: QueryFunction<OrganizationItemsResponseDto, QueryKey> = ({ pageParam, signal }) =>
    getOrganizationItems(
      queryData.organizationSlug,
      {
        ...queryData.params,
        page: pageParam ?? 0,
        size: queryData.params?.size ?? DEFAULT_PAGINATION?.size
      },
      signal
    );

  const getNextPageParam: GetNextPageParamFunction<OrganizationItemsResponseDto> = defaultGetNextPageParam(
    response => response.organizationItemDtos,
    queryData.params?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomFilterProcessesQueryOptions = (
  options: UseQueryOptions<ProcessResponseDTO[]>,
  queryData: { processQueryDTO: ProcessQueryDTO },
  _endpoint: { url: string }
): UseQueryOptions<ProcessResponseDTO[]> => {
  const filterProcesses = useFilterProcessesHook();

  const queryFn = defaultGetQueryFn(filterProcesses, queryData.processQueryDTO);

  const getNextPageParam: GetNextPageParamFunction<ProcessResponseDTO[]> = defaultGetNextPageParam(
    response => response,
    queryData.processQueryDTO?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomFilterTalentsQueryOptions = (
  options: UseQueryOptions<TalentsResponseDTO>,
  queryData: { talentListRequestDTO: TalentListRequestDTO; params?: FilterTalentsParams },
  _endpoint: { url: string }
): UseQueryOptions<TalentsResponseDTO> => {
  const filterTalents = useFilterTalentsHook();

  const queryFn: QueryFunction<TalentsResponseDTO, QueryKey> = ({ pageParam, signal }) =>
    filterTalents(
      {
        ...queryData.talentListRequestDTO,
        page: pageParam ?? 0,
        size: queryData.talentListRequestDTO?.size ?? DEFAULT_PAGINATION?.size
      },
      !pageParam ? queryData.params : { ...queryData.params, includeStats: false },
      signal
    );

  const getNextPageParam: GetNextPageParamFunction<TalentsResponseDTO> = defaultGetNextPageParam(
    response => response.talents,
    queryData.talentListRequestDTO?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomGetMidotUsersQueryOptions = (
  options: UseQueryOptions<MidotUsersResponseDTO>,
  queryData: { params: GetMidotUsersParams },
  _endpoint: { url: string }
): UseQueryOptions<MidotUsersResponseDTO> => {
  const getMidotUsers = useGetMidotUsersHook();

  const queryFn = defaultGetQueryFn(getMidotUsers, queryData.params);

  const getNextPageParam: GetNextPageParamFunction<MidotUsersResponseDTO> = defaultGetNextPageParam(
    response => response.users,
    queryData.params?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomFilterUserOrganizationAssignmentsQueryOptions = (
  options: UseQueryOptions<UserOrganizationAssignmentsResponseDTO>,
  queryData: { params: GetUserOrganizationAssignmentsParams },
  _endpoint: { url: string }
): UseQueryOptions<UserOrganizationAssignmentsResponseDTO> => {
  const filterUserOrganizationAssignments = useGetUserOrganizationAssignmentsHook();

  const queryFn = defaultGetQueryFn(filterUserOrganizationAssignments, queryData.params);

  const getNextPageParam: GetNextPageParamFunction<UserOrganizationAssignmentsResponseDTO> = defaultGetNextPageParam(
    response => response.userOrganizationAssignments,
    queryData.params?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomGetMidotRolesQueryOptions = (
  options: UseQueryOptions<MidotRolesResponseDTO>,
  queryData: { params: GetRolesParams },
  _endpoint: { url: string }
): UseQueryOptions<MidotRolesResponseDTO> => {
  const getMidotRoles = useGetRolesHook();

  const queryFn = defaultGetQueryFn(getMidotRoles, queryData.params);

  const getNextPageParam: GetNextPageParamFunction<MidotRolesResponseDTO> = defaultGetNextPageParam(
    response => response.roles,
    queryData.params?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomGetUserRoleAssignmentsByLocationQueryOptions = (
  options: UseQueryOptions<UserLocationRoleAssignmentsResponse>,
  queryData: { params: GetUserRoleAssignmentsByLocationParams; userId: string },
  _endpoint: { url: string }
): UseQueryOptions<UserLocationRoleAssignmentsResponse> => {
  const getUserRoleAssignmentsByLocation = useGetUserRoleAssignmentsByLocationHook();

  const queryFn: QueryFunction<UserLocationRoleAssignmentsResponse, QueryKey> = ({ pageParam, signal }) =>
    getUserRoleAssignmentsByLocation(queryData.userId, { ...queryData.params, page: pageParam }, signal);

  const getNextPageParam: GetNextPageParamFunction<UserLocationRoleAssignmentsResponse> = defaultGetNextPageParam(
    response => response.userLocationRoleAssignments,
    queryData.params?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};

export const useCustomGetProcessTalentsQueryOptions = (
  options: UseQueryOptions<PagedProcessTalentsResponseDTO>,
  queryData: { id: string; params?: GetProcessTalentsParams },
  _endpoint: { url: string }
): UseQueryOptions<PagedProcessTalentsResponseDTO> => {
  const getProcessTalents = useGetProcessTalentsHook();

  const queryFn: QueryFunction<PagedProcessTalentsResponseDTO, QueryKey> = ({ pageParam, signal }) =>
    getProcessTalents(
      queryData.id,
      {
        ...queryData.params,
        page: pageParam ?? 0,
        size: queryData.params?.size ?? DEFAULT_PAGINATION?.size
      },
      signal
    );

  const getNextPageParam: GetNextPageParamFunction<PagedProcessTalentsResponseDTO> = defaultGetNextPageParam(
    response => response.talents,
    queryData.params?.size
  );

  return {
    ...options,
    getNextPageParam,
    queryFn
  };
};
